/**
 * Same-origin Pages Function that fronts external lens/asset fetches.
 * See `functions/_proxy.js` at the repo root.
 */
const DEFAULT_PROXY_PATH = "/_proxy";
function isSameOrigin(url) {
    try {
        const currentOrigin = globalThis.location?.origin;
        return Boolean(currentOrigin) && new URL(url).origin === currentOrigin;
    }
    catch {
        return false;
    }
}
/**
 * Wraps a URL as a same-origin proxy URL so the engine fetches it through
 * this app's Pages Function instead of cross-origin. The function on the
 * server side validates the target host, attaches any service auth
 * (e.g. Cloudflare Access client headers), and streams the response back.
 *
 * Use as the engine's `rewriteFetchUrl` option:
 *
 * @example
 * import { lensProxyUrl } from "@lenscore-pipeline/lens-canvas";
 *
 * const engineOptions = {
 *   rewriteFetchUrl: lensProxyUrl,
 * };
 *
 * Already-relative URLs (no scheme) are passed through unchanged so callers
 * don't accidentally double-wrap local resources.
 *
 * @param {string} url
 * @returns {string}
 */
export function lensProxyUrl(url) {
    if (typeof url !== "string" || url.length === 0)
        return url;
    // Only proxy absolute URLs. Relative paths are already same-origin.
    if (!/^https?:\/\//i.test(url))
        return url;
    // Electron serves cached lenses from the renderer's own localhost origin.
    // Sending that URL through the cloud proxy both wastes a hop and asks
    // Cloudflare to fetch a private-loopback address, which is correctly denied.
    if (isSameOrigin(url))
        return url;
    return `${DEFAULT_PROXY_PATH}?url=${encodeURIComponent(url)}`;
}
/**
 * Returns a `rewriteFetchUrl`-shaped callback that wraps URLs as
 * `<proxyPath>?url=<encoded>`. Override the default `/_proxy` path when the
 * Pages Function is mounted elsewhere.
 *
 * @param {string} [proxyPath]
 * @returns {(url: string) => string}
 */
export function createLensProxyUrl(proxyPath = DEFAULT_PROXY_PATH) {
    const path = String(proxyPath || DEFAULT_PROXY_PATH).replace(/\/+$/, "") || DEFAULT_PROXY_PATH;
    return (url) => {
        if (typeof url !== "string" || url.length === 0)
            return url;
        if (!/^https?:\/\//i.test(url))
            return url;
        if (isSameOrigin(url))
            return url;
        return `${path}?url=${encodeURIComponent(url)}`;
    };
}
//# sourceMappingURL=lensProxyUrl.js.map